import { z } from 'zod'

import {
  ownedCardSchema,
  roomSchema,
  chatModelSchema,
  adultStatusSchema,
} from '@/features/veluvit/schemas/api.schema'
import { liveWorkSchema } from '@/features/veluvit/schemas/catalog.schema'

import type { Bootstrap } from '@/features/veluvit/types/api.types'

const id = z.string().min(1)
const userSchema = z.looseObject({
  id,
  name: z.string(),
  email: z.string().nullable(),
  guest: z.boolean(),
  persona: z.string(),
  settings: z.looseObject({
    showAdult: z.boolean().optional(),
    blockedCreators: z.array(z.string()).optional(),
  }),
})
const personaSchema = z.looseObject({
  id,
  name: z.string(),
  gender: z.string().default(''),
  age: z
    .union([z.string(), z.number()])
    .nullish()
    .transform((value) => (value == null ? '' : String(value))),
  role: z.string().default(''),
  appearance: z.string().default(''),
  personality: z.string().default(''),
  description: z.string().default(''),
  avatar: z
    .string()
    .nullish()
    .transform((value) => value ?? ''),
  isDefault: z.boolean(),
})
const attendanceSchema = z.looseObject({
  claimed: z.boolean(),
  streak: z.number().int().nonnegative().default(0),
  day: z.number().int(),
  rewards: z.array(
    z.looseObject({
      day: z.number().int(),
      gem: z.number().optional(),
      tickets: z.number().optional(),
    })
  ),
})
const ticketSchema = z.looseObject({
  id,
  status: z.string(),
  subject: z.string().optional(),
  body: z.string().optional(),
  replies: z.array(z.unknown()).optional(),
  locked: z.boolean().optional(),
})
const configSchema = z.looseObject({
  offline: z.boolean().default(false),
  testCharging: z.boolean(),
  prices: z.looseObject({
    inner: z.number(),
    record: z.number(),
    suggestions: z.number(),
    snap: z.number(),
  }),
  adult: adultStatusSchema.optional(),
  creator: z
    .looseObject({
      enabled: z.boolean().optional(),
      reviewRequired: z.boolean().optional(),
      creationReceipts: z.boolean().optional(),
    })
    .optional(),
  models: z.array(chatModelSchema).optional(),
  presets: z.array(z.looseObject({ id })).optional(),
  modes: z.array(z.looseObject({ id })).optional(),
})

const walletWireSchema = z
  .looseObject({
    balance: z.number(),
    lots: z.looseObject({
      paid: z.number(),
      bonus: z.number(),
      attendance: z.number(),
    }),
    charged: z.boolean().default(false),
    freeTurns: z.number(),
    unlockTickets: z.number(),
    totalTurns: z.number(),
    entries: z.array(
      z.looseObject({
        id,
        amount: z.number(),
        reason: z.string(),
        kind: z.string(),
        expiresAt: z.string().nullable(),
        createdAt: z.string(),
      })
    ),
    ticketEntries: z
      .array(
        z.looseObject({
          id,
          amount: z.number(),
          reason: z.string(),
          kind: z.string(),
          createdAt: z.string(),
        })
      )
      .default([]),
    nextTicketIn: z.number().optional(),
  })
  .transform((wallet) => ({
    ...wallet,
    nextTicketIn: wallet.nextTicketIn ?? 20 - (wallet.totalTurns % 20),
  }))

const bootstrapWireSchema = z.looseObject({
  user: userSchema.nullable(),
  works: z.array(liveWorkSchema),
  personas: z.array(personaSchema),
  wallet: walletWireSchema,
  rooms: z.array(roomSchema),
  favorites: z.array(id),
  hidden: z.array(id).default([]),
  ownedCards: z.array(ownedCardSchema),
  tickets: z.array(ticketSchema),
  attendance: attendanceSchema,
  config: configSchema,
})

export const bootstrapResponseSchema: z.ZodType<Bootstrap> =
  bootstrapWireSchema.transform((value) => value as unknown as Bootstrap)
